import { FC } from 'react';
import { useOutletContext } from 'react-router';
import { Content, Overlay, Portal, Description, Title, Close } from '@radix-ui/react-dialog';
import { Root as VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { route_path } from '@app/utils/route_path';
import { Button } from '@app/shared/ui/button';
import { cvaIcon, cvaScrollbar } from '@app/shared/ui/elements';
import { Skeleton } from '@app/shared/ui/skeleton';
import { CommonOutletContext } from '@app/app/layouts';
import { cn } from '@app/app/lib';

import { useAppSelector } from '@app/hooks/use-store';

import { usePaymentsDialogStore } from '@app/widgets/payments-dialog';

import { useTranslation, useLanguages } from '@app/app/i18n';
import { LocalizedLink, LocalizedNavLink } from '@app/shared/ui/localized-link';

import { isBoxedMode } from '@app/utils/user-status.utils';

import {
  BxGridAltIcon,
  CogIcon,
  CreditCardIcon,
  CrossIcon,
  CrownIcon,
  DownloadCloudIcon,
  LogoutIcon,
  PersonIcon,
  UserCircleIcon,
  WorldIcon,
} from '../../../components/ui/icons';

import { useHeaderStore } from '../model/header.store';
import { useNavLinks } from '../model/static-data';

import { LanguageSwitcher } from './components/language-switcher';

// Language switcher component
const LanguageSwitcherMenuItem: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { currentLanguage, t: tHeader } = useTranslation('header');
  const languages = useLanguages('header');

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  return (
    <LanguageSwitcher
      onClose={onClose}
      itemTextSize="text-[16px]"
      trigger={
        <button type="button" className={cvaMenuItem({ isDotHidden: true, isFontSize20: true })}>
          <WorldIcon className={cvaIcon()} />
          <span>{currentLang.flag}</span>
          <span>{tHeader('language.title')}</span>
        </button>
      }
    />
  );
};

import {
  cvaActionsMenuMobile,
  cvaCloseButton,
  cvaEmail,
  cvaInner,
  cvaMenuContentMobile,
  cvaNavMenuMobile,
  cvaSeparator,
  cvaBadge,
} from './mobile-menu-styles';
import { cvaMenuItem } from './header-styles';

type MobileMenuProps = {
  daysLeft: number | null;
  onLogOut: () => void;
  onClose: () => void;
};

export const MobileMenu: FC<MobileMenuProps> = (props) => {
  const { daysLeft, onLogOut, onClose } = props;
  const { t: tHeader } = useTranslation('header');

  const { isAuthenticated, isFirstAuthCheck } = useAppSelector((state) => state.authState);

  const { setEmailNotify, setShowSettings, user_ctx, context, commonContext } =
    useOutletContext<CommonOutletContext>();

  const product_balance = context?.product_balance;

  const { toggleHeaderMenuOpen } = useHeaderStore();
  const handleNavigationClick = () => toggleHeaderMenuOpen(false);

  const { setIsOpen: setShowPayments } = usePaymentsDialogStore();

  const isBoxed = isBoxedMode(commonContext);
  const isUserIdEven = user_ctx?.id !== undefined; // && user_ctx.id % 2 === 0;
  const isWorkspaceDefault = isBoxed || isUserIdEven;

  const navLinks = useNavLinks();
  // const navLinksV2 = useNavLinksV2();

  return (
    <Portal>
      <Overlay />
      {/* TODO: Replace font-inter */}
      <Content className={cn(cvaMenuContentMobile(), cvaScrollbar(), 'font-inter')}>
        <div className={cvaInner()}>
          {/* TODO: Accessibility component */}
          <VisuallyHidden>
            <Title />
            <Description />
          </VisuallyHidden>
          <div className={cvaActionsMenuMobile()}>
            {isFirstAuthCheck ? (
              <>
                <Skeleton className="h-12 w-full rounded-full" />
                <Skeleton className="h-12 w-full rounded-lg" />
              </>
            ) : !isAuthenticated ? (
              <>
                <Button
                  variant="outline"
                  size="large"
                  borderRadius="4xl"
                  onClick={handleNavigationClick}
                  asChild
                >
                  <LocalizedLink to="/register/">{tHeader('auth.signup')}</LocalizedLink>
                </Button>
                <LocalizedLink
                  to="/login/"
                  onClick={handleNavigationClick}
                  className={cvaMenuItem({ isDotHidden: true, isFontSize20: true })}
                >
                  <span className="inline-flex gap-x-1.5">
                    <PersonIcon className={cvaIcon()} />
                    <span>{tHeader('auth.login')}</span>
                  </span>
                </LocalizedLink>
              </>
            ) : (
              <>
                <Button variant="outline" size="large" asChild onClick={onClose} borderRadius="4xl">
                  <LocalizedLink
                    to={isWorkspaceDefault ? route_path.workspaceCreate : route_path.create}
                  >
                    {tHeader('auth.create')}
                  </LocalizedLink>
                </Button>
                <LocalizedNavLink
                  to="/projects/"
                  className={({ isActive }) =>
                    cvaMenuItem({ isActive, isDotHidden: true, isFontSize20: true })
                  }
                  onClick={onClose}
                >
                  <BxGridAltIcon className={cvaIcon()} />
                  <span>{tHeader('account.myPresentations')}</span>
                </LocalizedNavLink>
              </>
            )}
          </div>
          <hr className={cvaSeparator()} />
          {isAuthenticated && (
            <>
              <div className={cvaActionsMenuMobile()}>
                {user_ctx?.username && (
                  <div className={cvaEmail()}>
                    {product_balance?.is_active &&
                    !product_balance?.product.is_trial &&
                    product_balance.type === 'subscription' ? (
                      <CrownIcon
                        className={cn(cvaIcon(), 'text-secondary_warning_SI_400_warning')}
                      />
                    ) : (
                      <UserCircleIcon className={cvaIcon()} />
                    )}
                    <span>{user_ctx.username}</span>
                  </div>
                )}

                {!user_ctx?.is_confirmed && (
                  <button type="button" onClick={() => setEmailNotify(true)} className={cvaBadge()}>
                    {tHeader('account.confirmEmail')}
                  </button>
                )}

                {!product_balance?.is_active && product_balance?.product && (
                  <LocalizedNavLink
                    to="/pricing/"
                    className={cvaBadge()}
                    onClick={onClose}
                    dangerouslySetInnerHTML={{
                      __html: product_balance.product.is_trial
                        ? tHeader('account.trialExpired')
                        : tHeader('account.subscriptionExpired'),
                    }}
                  />
                )}

                {/* PayGo Product balance alert */}
                {product_balance?.is_active &&
                  product_balance?.product &&
                  !product_balance?.product.is_trial &&
                  product_balance.type === 'presentation' && (
                    <div className={cvaBadge()}>
                      <DownloadCloudIcon className={cvaIcon()} />
                      <span>
                        {tHeader('account.downloads', {
                          count: product_balance.count,
                          total: product_balance.product.count,
                        })}
                      </span>
                    </div>
                  )}

                {daysLeft !== null && (
                  <LocalizedNavLink to="/pricing/" className={cvaBadge()} onClick={onClose}>
                    {daysLeft > 0
                      ? tHeader('account.trialExpiresInDays', { days: daysLeft })
                      : tHeader('account.trialExpiresToday')}
                  </LocalizedNavLink>
                )}

                {!isBoxed && (
                  <button
                    type="button"
                    className={cvaMenuItem({ isDotHidden: true, isFontSize20: true })}
                    onClick={() => setShowPayments(true)}
                  >
                    <CreditCardIcon className={cvaIcon({ size: 'large' })} />
                    <span>
                      {!product_balance?.is_active && product_balance?.product
                        ? tHeader('account.upgradePlan')
                        : tHeader('account.managePlan')}
                    </span>
                  </button>
                )}
                {
                  <button
                    type="button"
                    className={cvaMenuItem({ isDotHidden: true, isFontSize20: true })}
                    onClick={() => setShowSettings(true)}
                  >
                    <CogIcon className={cvaIcon({ size: 'large' })} />
                    <span>{tHeader('account.settings')}</span>
                  </button>
                }
              </div>
              <hr className={cvaSeparator()} />
              {/* Language switcher */}
              <div className={cvaActionsMenuMobile()}>
                <LanguageSwitcherMenuItem onClose={onClose} />
              </div>
              <hr className={cvaSeparator()} />
            </>
          )}
          <nav className={cvaNavMenuMobile()}>
            {!isBoxed &&
              navLinks.map(({ to, title, id }) => {
                // Templates - статическая страница, используем обычную ссылку
                if (to === route_path.templates) {
                  return (
                    <a
                      key={id}
                      href="/templates/"
                      className={cvaMenuItem({
                        isDotHidden: true,
                        isTransparent: false,
                        isFontSize20: true,
                      })}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                        // Используем window.location.href для гарантированного перехода на статическую страницу
                        setTimeout(() => {
                          window.location.href = '/templates/';
                        }, 100);
                      }}
                    >
                      {title}
                    </a>
                  );
                }
                return (
                  <LocalizedNavLink
                    key={id}
                    className={({ isActive }) =>
                      cvaMenuItem({
                        isActive,
                        isDotHidden: true,
                        isTransparent: false,
                        isFontSize20: true,
                      })
                    }
                    to={to}
                    onClick={onClose}
                  >
                    {title}
                  </LocalizedNavLink>
                );
              })}
            {!isBoxed && (
              <>
                <a
                  href={route_path.blog}
                  className={cvaMenuItem({ isDotHidden: true, isFontSize20: true })}
                >
                  {tHeader('links.blog')}
                </a>
              </>
            )}
          </nav>
          {isAuthenticated && (
            <>
              <hr className={cvaSeparator()} />
              <a
                href={route_path.logout}
                className={cvaMenuItem({ isDotHidden: true, isFontSize20: true })}
                onClick={onLogOut}
              >
                <LogoutIcon className={cvaIcon({ size: 'large' })} />
                <span>{tHeader('auth.logout')}</span>
              </a>
              {user_ctx?.is_superuser && (
                <Button size="large" variant="outline" asChild className="mt-8">
                  <a href={route_path.admin}>{tHeader('account.admin')}</a>
                </Button>
              )}
            </>
          )}
          <Close className={cvaCloseButton()} onClick={onClose}>
            <CrossIcon className={cvaIcon({ size: 'large' })} />
          </Close>
        </div>
      </Content>
    </Portal>
  );
};
